import type { AudioClip, AutomationCurve, Clip, ClipGainPoint, Project } from './types';
import { curveShape } from './curves';
import { thinIndices } from './curveThin';
import { clipEndTicks, effectivePlaybackRate, tempoMapOf } from './selectors';
import { beatToSourceSec, hasWarpMarkers, sourceSecToBeat, warpCurve } from './warpMap';
import { secondsToTicks, tempoEventAt, ticksToSeconds } from '@/timing/tempoMap';

/**
 * A gain envelope drawn inside one clip.
 *
 * This is the level ride that a track automation lane cannot do: pulling one
 * word of a vocal down without touching the fader, and without splitting the
 * clip into three. It sits before the clip's static `gain` and before the fades,
 * so the three compose — an envelope does not fight a fade-in, it shapes what
 * the fade-in then fades.
 *
 * **Points are positioned in source seconds**, not in ticks on the timeline and
 * not relative to the clip's start. That one choice is what makes the envelope
 * stay on the audio:
 *
 * - Trimming the clip's start moves `offsetSec` and `startTicks` together, so
 *   the audio does not move. An envelope measured from the clip's start would
 *   slide off the word it was riding; measured in source seconds it does not
 *   move at all.
 * - Splitting a clip gives both halves the same source material at different
 *   offsets, so both keep the part of the envelope that belongs to them without
 *   any arithmetic.
 * - Stretching, warping and varispeed all change how source seconds map onto
 *   the timeline. The envelope is stated in the one coordinate they agree on,
 *   and the mapping is done once, where the clip is scheduled.
 *
 * The cost is that a ride cannot be pinned to a bar line. That is the right way
 * round: a level ride follows a performance, and the performance is in the
 * audio.
 *
 * It is also why nothing here has to be recomputed when a clip is split or
 * trimmed. A slice keeps the same asset at a different `offsetSec`, so the same
 * envelope, unchanged, already means the right thing on both pieces — each one
 * simply reads the part of it covering its own source span. Trimming therefore
 * never destroys a ride either: trim back out and it is still there.
 */

/**
 * Linear gain, so 1 is unity, and the ceiling is +6dB.
 *
 * A modest lift, not a rescue — and low enough that the whole range fits on a
 * clip's body as something you can aim at, with unity halfway up. A ride that
 * needs more than +6dB is a gain-staging problem, not an envelope.
 */
export const MIN_CLIP_GAIN = 0;
export const MAX_CLIP_GAIN = 2;

/**
 * Where a readout stops counting down.
 *
 * Zero gain is minus infinity, which is true and useless on a label. Sixty
 * decibels down is below anything a ride is used for, so a point at or under it
 * reads as silence rather than as a number nobody would type.
 */
export const RIDE_FLOOR_DB = -60;

/** A ride point's level as a musician reads it: signed decibels, one decimal. */
export function formatRideDb(gain: number): string {
  if (gain <= 0) return '−∞ dB';
  const db = 20 * Math.log10(gain);
  if (db <= RIDE_FLOOR_DB) return '−∞ dB';
  const rounded = Math.round(db * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toFixed(1)} dB`;
}

/** The gain a typed decibel value asks for, clamped to what a ride can hold. */
export function rideGainFromDb(db: number): number {
  if (!Number.isFinite(db) || db <= RIDE_FLOOR_DB) return MIN_CLIP_GAIN;
  return Math.min(MAX_CLIP_GAIN, Math.max(MIN_CLIP_GAIN, Math.pow(10, db / 20)));
}

/** Points in order. Commands keep them sorted; loading and imports are defensive. */
export function sortedGainPoints(points: readonly ClipGainPoint[]): ClipGainPoint[] {
  for (let i = 1; i < points.length; i++) {
    if (points[i].sourceSec < points[i - 1].sourceSec) {
      return [...points].sort((a, b) => a.sourceSec - b.sourceSec || a.id.localeCompare(b.id));
    }
  }
  return points as ClipGainPoint[];
}

/** The envelope on a clip, or an empty list for anything that cannot carry one. */
export function gainEnvelopeOf(clip: Clip | undefined): readonly ClipGainPoint[] {
  return clip && clip.kind === 'audio' ? clip.gainEnvelope : [];
}

/** True when a clip carries an envelope that will actually do something. */
export function hasGainEnvelope(clip: Clip | undefined): boolean {
  return gainEnvelopeOf(clip).length > 0;
}

/**
 * The envelope's gain at a position in the source audio.
 *
 * Held before the first point and after the last, for the same reason an
 * automation lane holds: an envelope describes the whole clip, and one that
 * silently reverted to unity outside its points would make a single point mean
 * nothing.
 */
export function clipGainAt(points: readonly ClipGainPoint[], sourceSec: number): number {
  const sorted = sortedGainPoints(points);
  if (sorted.length === 0) return 1;
  if (sourceSec <= sorted[0].sourceSec) return sorted[0].gain;

  const last = sorted[sorted.length - 1];
  if (sourceSec >= last.sourceSec) return last.gain;

  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid].sourceSec <= sourceSec) lo = mid;
    else hi = mid;
  }

  const a = sorted[lo];
  const b = sorted[lo + 1];
  const span = b.sourceSec - a.sourceSec;
  if (span <= 0) return b.gain;
  return a.gain + (b.gain - a.gain) * curveShape(a.curve, (sourceSec - a.sourceSec) / span);
}

/** The gain a clip applies at a source position: its envelope times its static gain. */
export function clipTotalGainAt(clip: AudioClip, sourceSec: number): number {
  return clip.gain * clipGainAt(clip.gainEnvelope, sourceSec);
}

export interface ClipGainEvent {
  /** Position in the source audio, in seconds. */
  sourceSec: number;
  gain: number;
  /** `step` jumps; `ramp` interpolates from the previous event. */
  kind: 'step' | 'ramp';
}

/** Sub-ramps used to approximate one smooth segment. */
const SMOOTH_STEPS = 24;

/**
 * Flattens an envelope into events covering the source span `[from, to]`.
 *
 * The first event is always an anchor at `from` carrying the value the envelope
 * already holds there, so a clip that is played from its middle — trimmed, or a
 * render that starts inside it — begins at the right level instead of jumping to
 * the next point.
 *
 * Positions stay in source seconds. Turning them into times on the timeline is
 * the scheduler's job, because only the scheduler knows the clip's rate, its
 * warp and where it sits.
 */
export function clipGainEvents(
  points: readonly ClipGainPoint[],
  fromSourceSec: number,
  toSourceSec: number,
): ClipGainEvent[] {
  const sorted = sortedGainPoints(points);
  if (sorted.length === 0) return [];

  const events: ClipGainEvent[] = [
    { sourceSec: fromSourceSec, gain: clipGainAt(sorted, fromSourceSec), kind: 'step' },
  ];
  if (toSourceSec <= fromSourceSec) return events;

  for (let i = 0; i < sorted.length; i++) {
    const point = sorted[i];
    if (point.sourceSec <= fromSourceSec) continue;
    if (point.sourceSec > toSourceSec) break;

    const previous = sorted[i - 1];
    const curve = previous?.curve ?? 'linear';

    if (!previous || curve === 'hold') {
      events.push({ sourceSec: point.sourceSec, gain: point.gain, kind: 'step' });
      continue;
    }

    if (curve === 'smooth') {
      const segStart = Math.max(previous.sourceSec, fromSourceSec);
      const segSpan = point.sourceSec - segStart;
      for (let s = 1; s <= SMOOTH_STEPS; s++) {
        const sourceSec = segStart + (segSpan * s) / SMOOTH_STEPS;
        events.push({ sourceSec, gain: clipGainAt(sorted, sourceSec), kind: 'ramp' });
      }
      continue;
    }

    events.push({ sourceSec: point.sourceSec, gain: point.gain, kind: 'ramp' });
  }

  // Hold the last value to the end of the span, so the gain does not drift back
  // to a stale scheduled value after the final point.
  const last = events[events.length - 1];
  if (last.sourceSec < toSourceSec) events.push({ sourceSec: toSourceSec, gain: last.gain, kind: 'ramp' });

  return events;
}

/** A point clamped into the shape the model allows. */
export function makeGainPoint(id: string, sourceSec: number, gain: number, curve: AutomationCurve): ClipGainPoint {
  return {
    id,
    sourceSec: Math.max(0, sourceSec),
    gain: Math.min(MAX_CLIP_GAIN, Math.max(MIN_CLIP_GAIN, gain)),
    curve,
  };
}

/**
 * Adds or replaces a point, keeping the list sorted.
 *
 * A point landing on top of an existing one replaces it rather than stacking:
 * two points at the same position make a segment of zero length, which is a
 * discontinuity nobody drew on purpose.
 */
export function withGainPoint(points: readonly ClipGainPoint[], point: ClipGainPoint): ClipGainPoint[] {
  const kept = points.filter((p) => p.id !== point.id && Math.abs(p.sourceSec - point.sourceSec) > 1e-6);
  return sortedGainPoints([...kept, point]);
}

/**
 * The points a straight line through their neighbours would have put in the
 * same place anyway, removed.
 *
 * The same pass an automation lane gets, and for the same reason: a ride
 * recorded from a fader, or drawn with a shaky hand, is hundreds of points
 * that say what a dozen would. Tolerance is in the ride's own normalised
 * units — a fraction of the full range, so `0.02` is the level being wrong by
 * a fiftieth of the way from silence to double — which is what the automation
 * thinning means by its tolerance too, so the same three settings serve both.
 *
 * Only runs of *linear* segments are thinned. The thinning judges a point by
 * the straight line between its neighbours, and a hold or a smooth segment is
 * not a straight line: a point that starts one, or ends one, is a decision
 * about shape and is kept. So is the first point and the last, because the
 * ride still has to begin and end where it did.
 */
export function thinGainPoints(points: readonly ClipGainPoint[], tolerance: number): ClipGainPoint[] {
  const sorted = sortedGainPoints(points);
  if (sorted.length <= 2) return sorted;
  const kept: ClipGainPoint[] = [];
  let runStart = 0;
  for (let i = 0; i < sorted.length; i++) {
    // A run ends at the last point whose own segment is still straight, and
    // at the end of the ride.
    const runEnds = i === sorted.length - 1 || sorted[i].curve !== 'linear';
    if (!runEnds) continue;
    const run = sorted.slice(runStart, i + 1);
    const keep = thinIndices(
      run.map((point) => ({ tick: point.sourceSec, value: point.gain / MAX_CLIP_GAIN })),
      tolerance,
    );
    for (const index of keep) kept.push(run[index]);
    runStart = i + 1;
  }
  return kept;
}

/** Removes points by id. */
export function withoutGainPoints(points: readonly ClipGainPoint[], ids: readonly string[]): ClipGainPoint[] {
  const doomed = new Set(ids);
  return points.filter((point) => !doomed.has(point.id));
}

/**
 * Where a moment inside a clip sits in its source audio, and back again.
 *
 * The envelope is stated in source seconds; everything that wants to draw it,
 * schedule it or move it between clips needs the other coordinate — seconds from
 * the clip's start on the timeline. Between the two sit the clip's rate and,
 * for a clip that follows the tempo, its warp curve through the tempo map.
 *
 * One implementation, shared by the scheduler and the clipboard, because two
 * would eventually disagree and the disagreement would be inaudible until it
 * was not.
 */
export function clipSourceAtOffset(project: Project, clip: AudioClip, offsetSec: number): number {
  const consumed = clipSourceConsumed(project, clip, offsetSec);
  // A reversed clip walks its own span backwards: the first thing heard is the
  // last thing recorded. So the ride a user drew over "the end of the word" is
  // still over the end of the word, wherever it now falls in time.
  return clip.reversed ? clip.offsetSec + clip.durationSec - consumed : clip.offsetSec + consumed;
}

export function clipOffsetAtSource(project: Project, clip: AudioClip, sourceSec: number): number {
  const consumed = clip.reversed ? clip.offsetSec + clip.durationSec - sourceSec : sourceSec - clip.offsetSec;
  return clipOffsetForConsumed(project, clip, consumed);
}

/**
 * The same mapping in ticks, which is what the timeline is drawn in.
 *
 * The drawing used to spread a clip's source evenly across its width, which is
 * right for most clips and quietly wrong for two kinds: one carrying warp
 * markers, whose middle has been pulled around on purpose, and a fixed-rate
 * clip lying under a tempo change, whose seconds are no longer a straight line
 * of ticks. On those the ride was heard where the scheduler put it and drawn
 * where the even spread put it. Going through the one mapping the scheduler
 * uses is what makes the pixel and the beat agree.
 */
export function clipTickAtSource(project: Project, clip: AudioClip, sourceSec: number): number {
  const map = tempoMapOf(project);
  const startSec = ticksToSeconds(map, clip.startTicks);
  return secondsToTicks(map, startSec + clipOffsetAtSource(project, clip, sourceSec));
}

export function clipSourceAtTick(project: Project, clip: AudioClip, tick: number): number {
  const map = tempoMapOf(project);
  const startSec = ticksToSeconds(map, clip.startTicks);
  return clipSourceAtOffset(project, clip, ticksToSeconds(map, tick) - startSec);
}

/**
 * True when source position is a straight line of ticks across the clip, so a
 * drawing can spread it evenly instead of asking the mapping per pixel.
 *
 * A clip that follows the tempo without markers is always even: it is a fixed
 * number of beats and the timeline is drawn in beats, whatever the tempo does
 * underneath. A fixed-rate clip is even only while the tempo is constant across
 * it — a change or a ramp inside it bends its seconds against the ruler.
 */
export function clipMapsEvenly(project: Project, clip: AudioClip): boolean {
  if (clip.warpSourceBpm !== null) return !hasWarpMarkers(clip);
  const map = tempoMapOf(project);
  const end = clipEndTicks(project, clip);
  for (const event of map.events) {
    if (event.tick > clip.startTicks && event.tick < end) return false;
  }
  const { event, ramps } = tempoEventAt(map, clip.startTicks);
  if (!ramps) return true;
  const next = map.events[map.events.indexOf(event) + 1];
  return next === undefined || next.bpm === event.bpm;
}

/** Source seconds a clip has consumed by `offsetSec` into itself, counting forwards. */
function clipSourceConsumed(project: Project, clip: AudioClip, offsetSec: number): number {
  if (clip.warpSourceBpm === null) return offsetSec * effectivePlaybackRate(project, clip);
  const map = tempoMapOf(project);
  const clipStartSec = ticksToSeconds(map, clip.startTicks);
  const beat = (secondsToTicks(map, clipStartSec + offsetSec) - clip.startTicks) / project.transport.ppq;
  // A warp curve is measured from where the clip starts reading, not from the
  // top of the file, so it already speaks in "consumed" terms.
  return beatToSourceSec(warpCurve(clip), beat);
}

/** The inverse: how far into the clip a given amount of consumed source is. */
function clipOffsetForConsumed(project: Project, clip: AudioClip, consumed: number): number {
  if (clip.warpSourceBpm === null) return consumed / effectivePlaybackRate(project, clip);
  const map = tempoMapOf(project);
  const beat = sourceSecToBeat(warpCurve(clip), consumed);
  return ticksToSeconds(map, clip.startTicks + beat * project.transport.ppq) - ticksToSeconds(map, clip.startTicks);
}

/**
 * An envelope lifted off a clip, ready to be put on another one.
 *
 * Points are carried as offsets from the clip's start rather than as source
 * positions, which is the only thing that makes a paste useful. Four takes of
 * the same phrase are four different assets; what they share is *where in the
 * clip* the phrase sits, not where in some file. Pasting therefore lands the
 * ride at the same place in the target clip, whatever it is reading.
 */
export interface ClipGainClipboard {
  points: { offsetSec: number; gain: number; curve: AutomationCurve }[];
}

export function copyClipGain(project: Project, clip: AudioClip): ClipGainClipboard | null {
  if (clip.gainEnvelope.length === 0) return null;
  return {
    points: sortedGainPoints(clip.gainEnvelope).map((point) => ({
      offsetSec: clipOffsetAtSource(project, clip, point.sourceSec),
      gain: point.gain,
      curve: point.curve,
    })),
  };
}

/** The envelope a clipboard becomes on a given clip. */
export function pasteClipGain(
  project: Project,
  clip: AudioClip,
  clipboard: ClipGainClipboard,
  idFor: (index: number) => string,
): ClipGainPoint[] {
  return sortedGainPoints(
    clipboard.points.map((point, index) =>
      makeGainPoint(idFor(index), clipSourceAtOffset(project, clip, point.offsetSec), point.gain, point.curve),
    ),
  );
}
